/**
 * rPI Porter — Channels API client (E-102 P2-1)
 *
 * Wraps: GET /api/v1/channels — owner-only tenant-scoped read of
 *        ChatwootAccountMapping mirror + webchat embed snippet + public form URL.
 *
 * SCOPE LOCK (P2-1 READ-ONLY):
 *  - Only the GET endpoint is wired here. PATCH/POST is P2-1b follow-up
 *    (Chatwoot Automation push on write).
 *  - Backend response mirrors ChannelsListView in channels.service.ts.
 */

import { api } from './client';

// ============================================
// TYPES — backend response shape
// ============================================

export type ChannelType = 'EMAIL' | 'WEBCHAT' | 'CONTACT_FORM';

export interface ChannelConfig {
  channelType: ChannelType;
  inboxId: number | null;
  configured: boolean;
}

export interface ChannelsResponse {
  chatwootAccountId: number;
  /** 'ACTIVE' | 'NOT_CONFIGURED' | future statuses from Phase 1 provisioning */
  status: string;
  channels: ChannelConfig[];
  /** Chatwoot webchat SDK <script> snippet; null when webchat inbox not configured. */
  webchatEmbedSnippet: string | null;
  /** Public inquiry form URL, e.g. https://rpi.mflerp.com/public/inquiry?tenant=<uuid> */
  publicFormUrl: string;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * GET /api/v1/channels — owner-only channel config + snippets.
 * Backend guards with OWNER role (RolesGuard) — caller must be authenticated.
 */
export async function getChannels(): Promise<{ data: ChannelsResponse }> {
  return api.get<{ data: ChannelsResponse }>('/channels');
}
